import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import { candidatesStore } from 'components/candidates/CandidatesStore';
import { candidateSeniorityOptions } from 'components/candidates/profile/scorecards/CandidateScorecardStore';
import {
	deserializeColumnState,
	serializeColumnState,
	normalizeColumnStateForCompare
} from './columnState';
import {
	getCurrentUser,
	getCurrentUserId,
	normalizeUserRefForCompare,
	resolveUserRef,
	serializeUserRef
} from './userRef';
import {
	resolveEntityRef,
	resolveEntityRefs,
	serializeEntityRef,
	serializeEntityRefs
} from './entityRef';
import {
	deserializeDateRange,
	normalizeEntityRefIds,
	normalizeQueryState,
	normalizeUiState,
	pickViewQueryForView,
	serializeDateRange,
	serializeOptionValues,
	captureSearchFilter,
	resolveSearchFilterForApply
} from './viewStateUtils';
import { syncFilterDrawerProps } from './syncFilterDrawerProps';

const EMPTY_QUERY = { sort: { column: 'createdAt', asc: false } };

const CANDIDATES_VIEW_QUERY_KEYS = [
	'text',
	'countryId',
	'englishLevel',
	'ownerId',
	'stage',
	'seniority',
	'city',
	'tags',
	'expectedCompensation',
	'currentCompensation',
	'jobRoleIds',
	'skillIds',
	'mineOnly',
	'createdAt',
	'updatedAt',
	'lastEngagementAt',
	'sourceId',
	'referrerId',
	'hireability',
	'publicProfileStatus'
];

const CANDIDATES_ENTITY_REF_KEYS = [
	'countryIds',
	'tagIds',
	'jobRoleIds',
	'skillIds'
];

const CANDIDATES_USER_REF_KEYS = ['ownerFilter', 'sourceFilter'];

function pickCandidatesQueryForView(query) {
	return pickViewQueryForView(
		query,
		CANDIDATES_VIEW_QUERY_KEYS,
		EMPTY_QUERY.sort
	);
}

export function getCandidatesFactoryUiState() {
	return {
		searchFilter: null,
		countryFilter: null,
		englishLevelFilter: [],
		ownerFilter: null,
		stageFilter: null,
		seniorityFilter: [],
		cityFilter: null,
		tagFilter: null,
		expectedCompensationFilter: null,
		currentCompensationFilter: null,
		jobRoleFilter: [],
		skillFilter: [],
		mineOnly: false,
		createdAtFilter: null,
		updatedAtFilter: null,
		lastEngagementAtFilter: null,
		sourceFilter: null,
		referrerFilter: null,
		hireabilityFilter: null,
		publicProfileStatusFilter: []
	};
}

export function captureCandidatesListView(page) {
	const currentUserId = getCurrentUserId();

	return {
		ui: {
			searchFilter: captureSearchFilter(
				page.state.searchFilter,
				page.listStore.store.query
			),
			countryIds: serializeEntityRefs(page.state.countryFilter),
			englishLevelFilter: serializeOptionValues(page.state.englishLevelFilter),
			ownerFilter: serializeUserRef(page.state.ownerFilter, currentUserId),
			stageFilter: serializeOptionValues(page.state.stageFilter),
			seniorityFilter: serializeOptionValues(page.state.seniorityFilter),
			cityFilter: page.state.cityFilter || null,
			tagIds: serializeEntityRefs(page.state.tagFilter),
			expectedCompensationFilter: page.state.expectedCompensationFilter || null,
			currentCompensationFilter: page.state.currentCompensationFilter || null,
			jobRoleIds: serializeEntityRefs(page.state.jobRoleFilter),
			skillIds: serializeEntityRefs(page.state.skillFilter),
			mineOnly: !!page.state.mineOnly,
			createdAtFilter: serializeDateRange(page.state.createdAtFilter),
			updatedAtFilter: serializeDateRange(page.state.updatedAtFilter),
			lastEngagementAtFilter: serializeDateRange(
				page.state.lastEngagementAtFilter
			),
			sourceFilter: serializeUserRef(page.state.sourceFilter, currentUserId),
			referrerFilter: serializeEntityRef(page.state.referrerFilter),
			hireabilityFilter: page.state.hireabilityFilter || null,
			publicProfileStatusFilter: serializeOptionValues(
				page.state.publicProfileStatusFilter
			)
		},
		query: pickCandidatesQueryForView(page.listStore.store.query),
		columnState: serializeColumnState(page.state.visibleColumns)
	};
}

export function getCandidatesFactorySnapshot() {
	return {
		ui: {
			searchFilter: null,
			countryIds: null,
			englishLevelFilter: null,
			ownerFilter: null,
			stageFilter: null,
			seniorityFilter: null,
			cityFilter: null,
			tagIds: null,
			expectedCompensationFilter: null,
			currentCompensationFilter: null,
			jobRoleIds: null,
			skillIds: null,
			mineOnly: false,
			createdAtFilter: null,
			updatedAtFilter: null,
			lastEngagementAtFilter: null,
			sourceFilter: null,
			referrerFilter: null,
			hireabilityFilter: null,
			publicProfileStatusFilter: null
		},
		query: {
			sort: { column: 'createdAt', asc: false }
		},
		columnState: serializeColumnState(applicantStore.defaultTableColumnsOptions)
	};
}

function mapSeniorityOptions(values) {
	return values
		?.map(value => {
			const option = candidateSeniorityOptions.find(
				item => item.value === value
			);
			return option ? { ...option, label: option.text } : null;
		})
		?.filter(Boolean);
}

function buildUiStateFromSaved(ui) {
	const currentUser = getCurrentUser();

	return {
		searchFilter: ui.searchFilter || null,
		countryFilter: resolveEntityRefs(ui.countryIds) || null,
		englishLevelFilter:
			ui.englishLevelFilter
				?.map(value => {
					const option = candidatesStore.englishLevelOptions.find(
						level => level.value === value
					);
					return option ? { ...option, label: option.text } : null;
				})
				?.filter(Boolean) || null,
		ownerFilter: resolveUserRef(ui.ownerFilter, currentUser),
		stageFilter:
			ui.stageFilter
				?.map(value => {
					return applicantStore.stageOptions.find(
						stage => stage.value === value
					);
				})
				?.filter(Boolean) || null,
		seniorityFilter: mapSeniorityOptions(ui.seniorityFilter) || null,
		cityFilter: ui.cityFilter || null,
		tagFilter: resolveEntityRefs(ui.tagIds) || null,
		expectedCompensationFilter: ui.expectedCompensationFilter || null,
		currentCompensationFilter: ui.currentCompensationFilter || null,
		jobRoleFilter: resolveEntityRefs(ui.jobRoleIds) || null,
		skillFilter: resolveEntityRefs(ui.skillIds) || null,
		mineOnly: !!ui.mineOnly,
		createdAtFilter: deserializeDateRange(ui.createdAtFilter),
		updatedAtFilter: deserializeDateRange(ui.updatedAtFilter),
		lastEngagementAtFilter: deserializeDateRange(ui.lastEngagementAtFilter),
		sourceFilter: resolveUserRef(ui.sourceFilter, currentUser),
		referrerFilter: resolveEntityRef(ui.referrerFilter),
		hireabilityFilter: ui.hireabilityFilter || null,
		publicProfileStatusFilter:
			ui.publicProfileStatusFilter
				?.map(value =>
					applicantStore.publicProfileStatusOptions.find(
						option => option.value === value
					)
				)
				?.filter(Boolean) || []
	};
}

function applyQueryFields(page, query) {
	if (!query) return;
	const fields = [
		'text',
		'countryId',
		'englishLevel',
		'ownerId',
		'stage',
		'seniority',
		'city',
		'tags',
		'expectedCompensation',
		'currentCompensation',
		'jobRoleIds',
		'skillIds',
		'mineOnly',
		'createdAt',
		'updatedAt',
		'lastEngagementAt',
		'sourceId',
		'referrerId',
		'hireability',
		'publicProfileStatus'
	];
	fields.forEach(field => {
		if (query[field] != null) {
			page.listStore.store.setQuery(field, query[field]);
		}
	});
}

export function applyCandidatesListView(
	page,
	snapshot,
	{ isFactoryDefault = false } = {}
) {
	const ui = buildUiStateFromSaved(snapshot.ui || {});
	ui.searchFilter = resolveSearchFilterForApply(
		ui.searchFilter,
		snapshot.query
	);
	const visibleColumns = isFactoryDefault
		? applicantStore.defaultTableColumnsOptions
		: deserializeColumnState(
				snapshot.columnState,
				applicantStore.tableColumnsOptions,
				applicantStore.defaultTableColumnsOptions
			);

	page.listStore.store.resetQuery();
	page.listStore.store.setQuery(
		'sort',
		snapshot.query?.sort || { column: 'createdAt', asc: false }
	);
	page.listStore.store.setQuery('text', ui.searchFilter || null);
	applyQueryFields(page, snapshot.query);
	page.listStore.store.setQuery('mineOnly', ui.mineOnly ? true : null);

	return new Promise(resolve => {
		page.setState({ ...ui, visibleColumns }, () => {
			syncFilterDrawerProps(page);
			page.handlePaginationChange(1);
			resolve();
		});
	});
}

export function applyCandidatesFactoryDefaults(page) {
	return applyCandidatesListView(page, getCandidatesFactorySnapshot(), {
		isFactoryDefault: true
	});
}

export function resolveCandidatesViewSnapshot(view) {
	if (view.systemKey === 'default') {
		return getCandidatesFactorySnapshot();
	}
	return {
		ui: view.filterState?.ui || {},
		query: view.filterState?.query || EMPTY_QUERY,
		columnState: view.columnState
	};
}

export function normalizeCandidatesSnapshot(snapshot) {
	const ui = normalizeUiState(snapshot.ui || {});

	CANDIDATES_ENTITY_REF_KEYS.forEach(key => {
		if (ui[key] != null) {
			ui[key] = normalizeEntityRefIds(ui[key]);
		}
	});
	CANDIDATES_USER_REF_KEYS.forEach(key => {
		if (ui[key] != null) {
			ui[key] = normalizeUserRefForCompare(ui[key]);
		}
	});
	if (ui.referrerFilter != null) {
		ui.referrerFilter = normalizeEntityRefIds(
			Array.isArray(ui.referrerFilter) ? ui.referrerFilter : [ui.referrerFilter]
		);
	}

	return {
		ui,
		query: normalizeQueryState(
			pickCandidatesQueryForView(snapshot.query || {}),
			EMPTY_QUERY.sort
		),
		columnState: normalizeColumnStateForCompare(
			snapshot.columnState,
			applicantStore.defaultTableColumnsOptions
		)
	};
}

export function serializeCandidatesViewForSave(snapshot) {
	return {
		filterState: {
			ui: snapshot.ui,
			query: snapshot.query
		},
		columnState: snapshot.columnState
	};
}
